import { useState } from "react";
import { ArrowLeft, Check, Globe } from "lucide-react";
import { BottomNav } from "./bottom-nav";

interface LanguageRegionPageProps {
  onBack: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  available: boolean;
}

interface RegionOption {
  code: string;
  name: string;
  currency: string;
  currencySymbol: string;
  flag: string;
  dateFormat: string;
  timeFormat: string;
  available: boolean;
}

const LANGUAGES: LanguageOption[] = [
  { code: "pt-BR", name: "Portuguese (Brazil)", nativeName: "Português (Brasil)", flag: "🇧🇷", available: true },
  { code: "en-US", name: "English (US)", nativeName: "English (US)", flag: "🇺🇸", available: false },
  { code: "es-ES", name: "Spanish (Spain)", nativeName: "Español (España)", flag: "🇪🇸", available: false },
  { code: "es-MX", name: "Spanish (Mexico)", nativeName: "Español (México)", flag: "🇲🇽", available: false },
  { code: "fr-FR", name: "French (France)", nativeName: "Français (France)", flag: "🇫🇷", available: false },
];

const REGIONS: RegionOption[] = [
  {
    code: "BR",
    name: "Brasil",
    currency: "BRL",
    currencySymbol: "R$",
    flag: "🇧🇷",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "24h",
    available: true,
  },
  {
    code: "US",
    name: "United States",
    currency: "USD",
    currencySymbol: "$",
    flag: "🇺🇸",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "12h",
    available: false,
  },
  {
    code: "ES",
    name: "España",
    currency: "EUR",
    currencySymbol: "€",
    flag: "🇪🇸",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "24h",
    available: false,
  },
  {
    code: "MX",
    name: "México",
    currency: "MXN",
    currencySymbol: "$",
    flag: "🇲🇽",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "24h",
    available: false,
  },
  {
    code: "FR",
    name: "France",
    currency: "EUR",
    currencySymbol: "€",
    flag: "🇫🇷",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "24h",
    available: false,
  },
];

interface OptionButtonProps {
  flag: string;
  primaryText: string;
  secondaryText: string;
  isSelected: boolean;
  isAvailable: boolean;
  onClick: () => void;
  showBorder: boolean;
}

function OptionButton({
  flag,
  primaryText,
  secondaryText,
  isSelected,
  isAvailable,
  onClick,
  showBorder,
}: OptionButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={!isAvailable}
      className={`w-full flex items-center justify-between p-4 transition-colors ${
        showBorder ? "border-b border-border" : ""
      } ${isAvailable ? "hover:bg-accent cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-3xl">{flag}</span>
        <div className="text-left">
          <div className="flex items-center gap-2">
            <p className="font-medium">{primaryText}</p>
            {!isAvailable && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                Em breve
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{secondaryText}</p>
        </div>
      </div>
      {isSelected && <Check className="w-5 h-5 text-primary" />}
    </button>
  );
}

export function LanguageRegionPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: LanguageRegionPageProps) {
  const [selectedLanguage, setSelectedLanguage] = useState("pt-BR");
  const [selectedRegion, setSelectedRegion] = useState("BR");

  const currentLanguage = LANGUAGES.find((lang) => lang.code === selectedLanguage)!;
  const currentRegion = REGIONS.find((region) => region.code === selectedRegion)!;

  const handleLanguageChange = (code: string, available: boolean) => {
    if (!available) return;
    setSelectedLanguage(code);
  };

  const handleRegionChange = (code: string, available: boolean) => {
    if (!available) return;
    setSelectedRegion(code);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Idioma e Região</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Language Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Globe className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-medium">Idioma do Aplicativo</h2>
              <p className="text-sm text-muted-foreground">Escolha o idioma da interface</p>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {LANGUAGES.map((language, index) => (
              <OptionButton
                key={language.code}
                flag={language.flag}
                primaryText={language.nativeName}
                secondaryText={language.name}
                isSelected={selectedLanguage === language.code}
                isAvailable={language.available}
                onClick={() => handleLanguageChange(language.code, language.available)}
                showBorder={index !== LANGUAGES.length - 1}
              />
            ))}
          </div>
        </section>

        {/* Region Section */}
        <section>
          <div className="mb-4">
            <h2 className="font-medium mb-1">País e Região</h2>
            <p className="text-sm text-muted-foreground">
              Define moeda, formato de data e configurações regionais
            </p>
          </div>

          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {REGIONS.map((region, index) => (
              <OptionButton
                key={region.code}
                flag={region.flag}
                primaryText={region.name}
                secondaryText={`${region.currencySymbol} ${region.currency} • ${region.dateFormat}`}
                isSelected={selectedRegion === region.code}
                isAvailable={region.available}
                onClick={() => handleRegionChange(region.code, region.available)}
                showBorder={index !== REGIONS.length - 1}
              />
            ))}
          </div>
        </section>

        {/* Current Settings Summary */}
        <section className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-3">Configurações Atuais</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-blue-800">Idioma:</span>
              <span className="font-medium text-blue-900">
                {currentLanguage.flag} {currentLanguage.nativeName}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-800">Região:</span>
              <span className="font-medium text-blue-900">
                {currentRegion.flag} {currentRegion.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-800">Moeda:</span>
              <span className="font-medium text-blue-900">
                {currentRegion.currencySymbol} {currentRegion.currency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-800">Formato de data:</span>
              <span className="font-medium text-blue-900">{currentRegion.dateFormat}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-800">Formato de hora:</span>
              <span className="font-medium text-blue-900">{currentRegion.timeFormat}</span>
            </div>
          </div>
        </section>

        {/* Regional Features Info */}
        <section className="bg-card rounded-xl border border-border p-4">
          <h3 className="font-medium mb-3">Recursos Regionais</h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            {[
              {
                title: "Moeda",
                description: "Todos os preços serão exibidos na moeda da sua região",
              },
              {
                title: "Formato de Data",
                description: "Datas seguem o padrão regional (DD/MM/YYYY para Brasil)",
              },
              {
                title: "Impostos",
                description: "Cálculos de impostos e taxas seguem as regras locais",
              },
              {
                title: "Frete",
                description: "Opções de envio e valores baseados na sua região",
              },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <p>
                  <strong className="text-foreground">{item.title}:</strong> {item.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Info Box */}
        <section className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <p className="text-sm text-orange-900">
            <strong>Nota:</strong> Alterar o idioma ou região pode afetar a disponibilidade de
            alguns recursos e métodos de pagamento. As alterações serão aplicadas imediatamente.
          </p>
        </section>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}